<template>
    <div class="clinic-consent-template-tab" v-loader.opacity.tab="loading">
      <div class="clinic-template-list" id="consent-template-list" v-if="!isEditTemplate">
        <BaseTable class="no-border" v-if="tableTemplates" :tabledata="tableTemplates" @item-opened="toDetails">
          <template v-slot:column_templateName="{item}">
            <span class="cell-ellipsis" :title="item.name">{{ item.name }}</span>
          </template>

          <template v-slot:column_templateDescription="{item}">
            <span class="cell-ellipsis" :title="item.description">{{ item.description }}</span>
          </template>

          <template v-slot:column_actions="{item}">
            <div class="flex-center">
              <button class="btn btn-danger" type="button" name="button" data-test="btnDelete" @click.prevent.stop="confirmRemoval(item)" v-if="!item.isDefault">
                {{ $t('remove') }}
              </button>
            </div>
          </template>
        </BaseTable>
  
        <button class="btn-icon btn-top" type="button" name="button" v-if="tableTemplates && tableTemplates.items.length > 20" @click="goTop">
          <IconArrowUp/>
        </button>
      </div>
  
      <div class="clinic-edit-box" v-else>
        <ClinicConsentTemplateEdit :template_id="template ? template.id : null" @cancel-edit="cancelEditTemplate"/>
      </div>
  
      <!-- TODO: I'll return in a second stage -->
      <div class="action-bar" v-if="!isEditTemplate && isGlobalClient" @wheel.prevent>
        <button class="btn btn-success" type="button" name="button" @click="isEditTemplate = !isEditTemplate">
          <span>{{ $t('clinic.newConsentTemplate') }}</span>
        </button>
      </div>

      <BaseGenericOffcanvas :offcanvas_id="'offcanvasConsentTemplateGeneric'" ref="offcanvasGeneric"/>
    </div>
  </template>
  
  <style lang="scss" scoped>
  .clinic-template-list {
    padding:                          0 10px 10px 10px;
    flex:                             1;

    :deep(.vtable) {
      table-layout:                   fixed;
    }
  }

  .cell-ellipsis {
    display:                          block;
    overflow:                         hidden;
    text-overflow:                    ellipsis;
    white-space:                      nowrap;
  }

  @media (min-width: 992px) {
    .clinic-consent-template-tab {
      display:                        flex;
      flex-direction:                 column;
      flex:                           1;
      min-height:                     0;
    }

    .clinic-template-list {
      display:                        flex;
      flex-direction:                 column;
      min-height:                     0;
      overflow:                       hidden;
    }

    .clinic-edit-box {
      display:                        flex;
      flex-direction:                 column;
      flex:                           1;
      min-height:                     0;
    }
  }

  @media (max-width: 991px) {
    .clinic-template-list {
      height:                         calc(100dvh - 258px);
      max-height:                     calc(100dvh - 258px);
      padding:                        0;
      overflow-y:                     auto;
      overscroll-behavior:            contain;
      scrollbar-width:                none;

      &::-webkit-scrollbar {
        display:                      none;
      }

      :deep(.inside-box-mob) {
        max-height:                   calc(100dvh - 258px) !important;
      }
    }

    .has-alert {
      .clinic-template-list {
        height:                       calc(100dvh - 336px);
        max-height:                   calc(100dvh - 336px);
        :deep(.inside-box-mob) {
          max-height:                 calc(100dvh - 336px) !important;
        }
      }
    }

    .has-usage-bar {
      .clinic-template-list {
        height:                       calc(100dvh - 286px);
        max-height:                   calc(100dvh - 286px);
        :deep(.inside-box-mob) {
          max-height:                 calc(100dvh - 286px) !important;
        }
      }
    }
  }
  
  @media screen and (min-width: 992px) and (max-width: 1365px) {
    .clinic-template-list {
      // height:                         calc(100dvh - 309px); // replaced by flex: 1 in _scaffold.scss
    }

    .has-alert {
      .clinic-template-list {
        // height:                       calc(100dvh - 373px); // replaced by flex: 1 in _scaffold.scss
        // max-height:                   calc(100dvh - 373px); // replaced by flex: 1 in _scaffold.scss
      }
    }
  }
  </style>
  
  <script>
  import { useUserStore } from '@/store/user'
  import ClinicConsentTemplateEdit from './ClinicConsentTemplateEdit.vue'
  
  export default {
    name: 'ClinicConsentTemplateTab',
    components: {
      ClinicConsentTemplateEdit,
    },
    data: () => ({
      loading: false,
      tableTemplates: null,
      isEditTemplate: false,
      template: null,
      session: {}
    }),
    computed: {
      isGlobalClient() {
        return this.session?.isGlobalClient ?? false;
      },
      canRemoveTemplates() {
        return this.isGlobalClient && (this.session?.isPowerfull ?? false);
      }
    },
    mounted() {
      const userStore = useUserStore();
      this.session = userStore.sessionInfo;

      this.fetchData();
    },
    methods: {
      goTop() {
        const el = document.getElementById('consent-template-list')
        if (el) el.scrollTop = 0
      },
      async fetchData() {
        this.loading = true
        NProgress.start()
        try {
          let response = await this.$clinicService.listConsentTemplates({
            clinicID: this.session.currentClinic.id,
          });

          const clinicLocale = this.session.currentClinic.locale;
          const allTemplates = response.result || [];

          const customTemplates = allTemplates.filter(template => !template.isDefault);
          const defaultTemplates = allTemplates.filter(template => template.isDefault);

          const matchingDefault = defaultTemplates.find(template => (template.language || '') === clinicLocale);
          const fallbackDefault = defaultTemplates.find(template => template.language === 'en');
          const chosenDefault = matchingDefault || fallbackDefault;

          const filteredTemplates = chosenDefault ? [...customTemplates, chosenDefault] : customTemplates;
  
          this.tableTemplates = {
            'items': filteredTemplates,
            'columns': [
              {key: 'templateName', name: this.$i18n.t('name'), type: 'custom', value: 'name', width: '35%'},
              {key: 'templateDescription', name: this.$i18n.t('description'), type: 'custom', value: 'description', width: '65%'},
            ]
          }

          if (this.canRemoveTemplates) {
            this.tableTemplates.columns[1].width = '55%'
            this.tableTemplates.columns.push({key: 'actions', name: this.$i18n.t('clinic.actions'), type: 'custom', value: 'actions', width: '10%', align: 'text-center'})
          }
        } catch (err) {
          this.$message.popup(this, 'request_error', 'operationFail', 'error')
        }
        NProgress.done(true)
        this.loading = false
      },
      toDetails(values) {
        if (!values || !values.item) return
        this.template = values.item;
        this.isEditTemplate = true;
      },
      async confirmRemoval(item) {
        const ok = await this.$refs.offcanvasGeneric.show({
          modalType: 'danger',
          title: this.$i18n.t('confirm'),
          text: this.$i18n.t('general.removeConfirm'),
          okButton: this.$i18n.t('remove'),
          okButtonClass: 'btn-danger',
        })
        if (ok) {
          this.doDelete(item)
        }
      },
      async doDelete(item) {
        this.loading = true
        NProgress.start()
        try {
          await this.$clinicService.deleteConsentTemplate({
            clinicID: this.session.currentClinic.id,
            templateID: item.id,
          })
          this.$message.popup(this, 'general.removedSuccess', 'operationSuccess', 'success')
        } catch (err) {
          this.$message.popup(this, 'general.removedError', 'operationFail', 'error')
        }
        NProgress.done(true)
        this.loading = false
        this.fetchData()
      },
      cancelEditTemplate() {
        this.template = null
        this.isEditTemplate = false;
        this.fetchData();
      }
    }
  }
  </script>